import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ProfileHeader from "@/components/profile/ProfileHeader";
import { ProfileBio } from "@/components/profile/ProfileBio";
import { ProfileSkills } from "@/components/profile/ProfileSkills";
import { Code, User, Building, Phone, School, Briefcase, ArrowLeft, FolderKanban } from "lucide-react";
import { useUserStore } from "@/store/userStore";
import { useFreelancerStore } from "@/store/freelancerStore";
import { useToast } from "@/hooks/use-toast";
import { useEnterpriseStore } from "@/store/enterpriseStore";
import { EnterpriseProfileProjects } from "@/components/profile/EnterpriseProfileProjects";
import EnterpriseProfileHeader from "@/components/profile/EnterpriseProfileHeader";
import { EnterpriseCompanyInfo } from "@/components/profile/EnterpriseCompanyInfo";
import { EnterpriseContactInfo } from "@/components/profile/EnterpriseContactInfo";
import { ProfileEducation } from "@/components/profile/ProfileEducation";
import { ProfileProjects } from "@/components/profile/ProfileProjects";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FreelancerProfile } from "@/store/jobApplyStore";
import { PlatformProjects } from "@/components/profile/PlatformProjects";

interface PreviousProject {
  projectName: string;
  description: string;
  budget: number;
  completedOn: string;
  _id: string;
}

type ProfileProps = {
  backToAIRecommFreelancer?: (freelancerData: string) => void;
  backToProject?: any;
  newFreelancerData?: any | null;
  isFromContracPage?: boolean;
  newEnterpriseData?: any | null;
  isClientProfile?: boolean
};

const Profile: React.FC<ProfileProps> = ({ backToAIRecommFreelancer, newFreelancerData, newEnterpriseData, isFromContracPage, isClientProfile, backToProject}) => {
  
  const { toast } = useToast();
  const {
    profile: profileData,
    fetchFreelancerProfile,
    updateFreelancerProfile,
    uploadFreelancerProfilePicture
  } = useFreelancerStore();
  const {
    profile: enterpriseProfile,
    fetchEnterpriseProfile,
    updateEnterpriseProfile,
    uploadEnterpriseProfilePicture,
  } = useEnterpriseStore();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    ...profileData,
    profilePicture: profileData?.profilePicture || "",
    fullName: profileData?.fullName || "",
    designation: profileData?.designation || "",
    totalEarnings: profileData?.totalEarnings || 0,
    projectsCompleted: profileData?.projectsCompleted || 0,
    category: profileData?.category || "",
    experience: profileData?.experience || "",
    dailyRate: profileData?.dailyRate || 0,
    phone: profileData?.phone || "",
    rating: profileData?.rating || 0,
    location: profileData?.location || "",
    skills: profileData?.skills || [],
    availability: profileData?.availability ? "available" : "offline",
    education: profileData?.education || [],
    project: profileData?.project || [],
    completedJobs: profileData?.completedJobs || []
  });

  // Enterprise form data
  const [enterpriseFormData, setEnterpriseFormData] = useState({
    companyName: enterpriseProfile?.companyName || "",
    industryType: enterpriseProfile?.industryType || "",
    logo: enterpriseProfile?.logo || "",
    contactPerson: {
      name: enterpriseProfile?.contactPerson?.name || "",
      email: enterpriseProfile?.contactPerson?.email || "",
      phone: enterpriseProfile?.contactPerson?.phone || "",
    },
    location: {
      address: enterpriseProfile?.location?.address || "",
      city: enterpriseProfile?.location?.city || "",
      country: enterpriseProfile?.location?.country || "",
    },
    preferredCurrency: enterpriseProfile?.preferredCurrency || "",
    preferredPaymentMethod: enterpriseProfile?.preferredPaymentMethod || "",
    vatOrGstId: enterpriseProfile?.vatOrGstId || "",
    socialMediaProfile: enterpriseProfile?.socialMediaProfile || "",
    rating: enterpriseProfile?.rating || 0,
    previousProjects: enterpriseProfile?.previousProjects || [],
    postedJobs: enterpriseProfile?.postedJobs || [],
    reviews: enterpriseProfile?.reviews || []
  });
  const [tempEnterpriseFormData, setTempEnterpriseFormData] = useState(enterpriseFormData);

  const { user, accessToken } = useUserStore();
  const [searchParams] = useSearchParams();
  const freelancerId = searchParams.get("freelancerId") || newFreelancerData?._id;
  const [activeTab, setActiveTab] = useState(
    ((user.role === "Enterprise" && !freelancerId) || isClientProfile) ? "company" : "bio"
  );

  // Sync form data with profile store
  useEffect(() => {
    setFormData({
      ...profileData,
      profilePicture: profileData?.profilePicture || "",
      fullName: profileData?.fullName || "",
      designation: profileData?.designation || "",
      totalEarnings: profileData?.totalEarnings || 0,
      projectsCompleted: profileData?.projectsCompleted || 0,
      category: profileData?.category || "",
      experience: profileData?.experience || "",
      dailyRate: profileData?.dailyRate || 0,
      phone: profileData?.phone || "",
      rating: profileData?.rating || 0,
      location: profileData?.location || "",
      skills: profileData?.skills || [],
      availability: profileData?.availability ? "available" : "offline",
      project: profileData?.project || [],
      completedJobs: profileData?.completedJobs || []
    });
  }, [profileData]);

  // Sync enterprise form data
  useEffect(() => {
    const newFormData = {
      companyName: enterpriseProfile?.companyName || "",
      industryType: enterpriseProfile?.industryType || "",
      logo: enterpriseProfile?.logo || "",
      contactPerson: {
        name: enterpriseProfile?.contactPerson?.name || "",
        email: enterpriseProfile?.contactPerson?.email || "",
        phone: enterpriseProfile?.contactPerson?.phone || "",
      },
      location: {
        address: enterpriseProfile?.location?.address || "",
        city: enterpriseProfile?.location?.city || "",
        country: enterpriseProfile?.location?.country || "",
      },
      preferredCurrency: enterpriseProfile?.preferredCurrency || "",
      preferredPaymentMethod: enterpriseProfile?.preferredPaymentMethod || "",
      vatOrGstId: enterpriseProfile?.vatOrGstId || "",
      socialMediaProfile: enterpriseProfile?.socialMediaProfile || "",
      rating: enterpriseProfile?.rating || 0,
      previousProjects: enterpriseProfile?.previousProjects || [],
      postedJobs: enterpriseProfile?.postedJobs || [],
      reviews : enterpriseProfile?.reviews || []
    };
    setEnterpriseFormData(newFormData);
    setTempEnterpriseFormData(newFormData);
  }, [enterpriseProfile]);

useEffect(() => {
  if (newEnterpriseData) {
    setTempEnterpriseFormData(newEnterpriseData);
  }
}, [newEnterpriseData]);

  useEffect(() => {
    if (user) {
      if (freelancerId) {
        fetchFreelancerProfile(user.userId, user.accessToken, newFreelancerData);
      } else if (user.role === "freelancer") {
        fetchFreelancerProfile(user.userId, user.accessToken);
      } else {
        fetchEnterpriseProfile(user.userId, user.accessToken);
      }
    }
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleEnterpriseInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name.includes("contactPerson.") || name.includes("location.")) {
      const [parent, child] = name.split(".");
      setTempEnterpriseFormData((prev) => ({
        ...prev,
        [parent]: {
          ...(typeof prev[parent] === "object" && prev[parent] !== null
            ? prev[parent]
            : {}),
          [child]: value,
        },
      }));
    } else if (name.includes("previousProjects[")) {
      const match = name.match(/previousProjects\[(\d+)\]\.(.+)/);
      if (match) {
        const index = parseInt(match[1], 10);
        const field = match[2] as keyof PreviousProject;
        setTempEnterpriseFormData((prev) => {
          const updatedProjects = [...(prev.previousProjects || [])];
          updatedProjects[index] = {
            ...updatedProjects[index],
            [field]: field === "budget" ? Number(value) : value,
          };

          return {
            ...prev,
            previousProjects: updatedProjects,
          };
        });
      }
    } else {
      setTempEnterpriseFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleCancel = () => {
    setTempEnterpriseFormData(enterpriseFormData);
  };

  return (
    <div className={`container mx-auto px-4 ${isClientProfile ? '' : 'py-8'}`}>
      <div className="max-w-6xl mx-auto">
        {freelancerId &&
        <Button variant="ghost" className="mb-4" onClick={() => backToAIRecommFreelancer(freelancerId)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {isFromContracPage ? "Back to Contracts & Agreements" : "Back to AI Recommended Freelancer"}
        </Button>
        }
        {isClientProfile &&
        <Button variant="ghost" className="mb-4" onClick={backToProject}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Project
        </Button>
        }
        <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
          {((user?.role === "Enterprise" && !freelancerId) || isClientProfile)
            ? "Enterprise Profile"
            : "Professional Profile"}
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 space-y-6">
            <Card className="hover:shadow-lg transition-all duration-300 border-b-4 border-b-emerald-500 animate-fade-in overflow-hidden">
              {(user?.role === "Enterprise" && !freelancerId) || isClientProfile ? (
                <EnterpriseProfileHeader
                  isEditing={isEditing}
                  formData={tempEnterpriseFormData}
                  handleInputChange={handleEnterpriseInputChange}
                  setIsEditing={setIsEditing}
                  setFormData = {setFormData}
                  updateEnterpriseProfile={updateEnterpriseProfile}
                  uploadEnterpriseProfilePicture={uploadEnterpriseProfilePicture}
                  accessToken={accessToken}
                  userId={user?.userId || ""}
                  toast={toast}
                  handleCancel={handleCancel}
                  isClientProfile={isClientProfile}
                />
              ) : (
                <ProfileHeader
                  isEditing={isEditing}
                  formData={{
                    ...formData,
                    fullName: String(formData.fullName),
                    experience: String(formData.experience),
                    availability:
                      formData.availability === "available"
                        ? "available"
                        : "offline",
                  }}
                  handleInputChange={handleInputChange}
                  setIsEditing={setIsEditing}
                  setFormData={setFormData}
                  updateFreelancerProfile={updateFreelancerProfile}
                  uploadFreelancerProfilePicture={uploadFreelancerProfilePicture}
                  accessToken={accessToken}
                  userId={user?.userId || ""}
                  toast={toast}
                  freelancerId={freelancerId}
                />
              )}
            </Card>

            {/* <div className="hidden lg:block">
              <ProfileAIAssistant />
            </div> */}
          </div>

          <div
            className="lg:col-span-2 space-y-6 animate-fade-in"
            style={{ animationDelay: "0.2s" }}
          >
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="flex justify-evenly mb-6 bg-white border border-gray-200 p-1 rounded-xl shadow-sm">
                {((user?.role === "Enterprise" && !freelancerId) || isClientProfile) && (
                  <>
                    <TabsTrigger
                      value="company"
                      className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r px-4 data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                    >
                      <Building className="h-4 w-4" /> Company
                    </TabsTrigger>
                    <TabsTrigger
                      value="contact"
                      className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r px-4 data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                    >
                      <Phone className="h-4 w-4" /> Contact
                    </TabsTrigger>
                  </>
                )}
                {(user.role !== "Enterprise" || freelancerId) && !isClientProfile && (
                  <TabsTrigger
                    value="bio"
                    className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r px-4 data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                  >
                    <User className="h-4 w-4" /> Bio
                  </TabsTrigger>
                )}
                <TabsTrigger
                  value="skills"
                  className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r px-4 data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                >
                  <Code className="h-4 w-4" />{" "}
                  {((user?.role === "Enterprise" && !freelancerId) || isClientProfile ) ? "Work History" : "Skills"}
                </TabsTrigger>
                {((user.role !== "Enterprise" || freelancerId) && !isClientProfile) && (
                  <TabsTrigger
                    value="education"
                    className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r px-4 data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                  >
                    <School className="h-4 w-4" /> Education
                  </TabsTrigger>
                )}
                {((user.role !== "Enterprise" || freelancerId) && !isClientProfile) && (
                  <TabsTrigger
                    value="project"
                    className="flex items-center gap-1.5  data-[state=active]:bg-gradient-to-r px-4 data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                  >
                    <Briefcase className="h-4 w-4" /> Work History
                  </TabsTrigger>
                )}

                <TabsTrigger
                  value="platformProject"
                  className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r px-4 data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                >
                  <FolderKanban className="h-4 w-4" /> Platform Project
                </TabsTrigger>
        
                {/* <TabsTrigger
                  value="activity"
                  className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                >
                  <Activity className="h-4 w-4" /> Activity
                </TabsTrigger>
                <TabsTrigger
                  value="ai"
                  className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-emerald-700 rounded-lg lg:hidden transition-all"
                >
                  <Brain className="h-4 w-4 text-emerald-500" /> AI Assistant
                </TabsTrigger>
                <TabsTrigger
                  value="vcard"
                  className="flex items-center gap-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-50 data-[state=active]:to-emerald-50 data-[state=active]:text-blue-700 rounded-lg transition-all"
                >
                  <IdCard className="h-4 w-4" /> vCard
                </TabsTrigger> */}
              </TabsList>

              {(user.role !== "Enterprise" || freelancerId) && !isClientProfile && (
                <TabsContent
                  value="bio"
                  className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  <ProfileBio accessToken={accessToken} formData={formData} freelancerId={freelancerId}/>
                </TabsContent>
              )}
              {((user?.role === "Enterprise" && !freelancerId) || isClientProfile) && (
                <>
                  <TabsContent
                    value="company"
                    className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                  >
                    <EnterpriseCompanyInfo
                      isClientProfile={isClientProfile}
                      formData={tempEnterpriseFormData}
                      handleInputChange={handleEnterpriseInputChange}
                      updateEnterpriseProfile={updateEnterpriseProfile}
                      handleCancel={handleCancel}
                    />
                  </TabsContent>
                  <TabsContent
                    value="contact"
                    className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                  >
                    <EnterpriseContactInfo
                      isClientProfile={isClientProfile}
                      formData={tempEnterpriseFormData}
                      handleInputChange={handleEnterpriseInputChange}
                      updateEnterpriseProfile={updateEnterpriseProfile}
                      handleCancel={handleCancel}
                    />
                  </TabsContent>
                </>
              )}

              <TabsContent
                value="skills"
                className="mt-0 focus-visible:outline-none focus-visible:ring-0"
              >
                {((user?.role === "Enterprise" && !freelancerId) || isClientProfile) ? (
                  <EnterpriseProfileProjects
                    formData={tempEnterpriseFormData}
                    setFormData={setEnterpriseFormData}
                    handleInputChange={handleEnterpriseInputChange}
                    updateEnterpriseProfile={updateEnterpriseProfile}
                    handleCancel={handleCancel}
                  />
                ) : (
                  <ProfileSkills
                    accessToken={accessToken}
                    formData={formData}
                    setFormData={setFormData}
                    freelancerId={freelancerId}
                  />
                )}
              </TabsContent>
              {(user.role !== "Enterprise" || freelancerId) && (
                <TabsContent
                  value="education"
                  className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  <ProfileEducation accessToken={accessToken} formData={formData} freelancerId={freelancerId} />
                </TabsContent>
              )}
              {(user.role !== "Enterprise" || freelancerId) && (
                <TabsContent
                  value="project"
                  className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  <ProfileProjects accessToken={accessToken} formData={formData} freelancerId={freelancerId} />
                </TabsContent>
              )}

               {(user.role !== "Enterprise" || freelancerId) && ( 
                <TabsContent
                  value="platformProject"
                  className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  <PlatformProjects accessToken={accessToken} projects={formData.completedJobs} freelancerId={freelancerId} />
                </TabsContent>
              )}

               {(user.role === "Enterprise") && (
                <TabsContent
                  value="platformProject"
                  className="mt-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  <PlatformProjects accessToken={accessToken} projects={enterpriseFormData.postedJobs} freelancerId={freelancerId} />
                </TabsContent>
              )}

              {/* <TabsContent
                value="activity"
                className="mt-0 focus-visible:outline-none focus-visible:ring-0"
              >
                <ProfileActivity />
              </TabsContent>

              <TabsContent
                value="ai"
                className="mt-0 focus-visible:outline-none focus-visible:ring-0 lg:hidden"
              >
                <ProfileAIAssistant />
              </TabsContent>

              <TabsContent
                value="vcard"
                className="mt-0 focus-visible:outline-none focus-visible:ring-0"
              >
                <VCardTab />
              </TabsContent> */}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
